import { navigate } from "gatsby";
import {
  getSessionToken,
  isBrowser,
  isSessionExpired,
  logout,
} from "./auth.mjs";

/**
 * Central handling for API responses that reject our credentials.
 *
 * Covers the manager JWT in localStorage (property managers and marketers),
 * used by extension.mjs and mobile-backend.ts. The condo "access" token from
 * /v1/login is not routed through here - those pages clear it inline.
 *
 * A 401 does not always mean the session died: the extension also answers 401
 * when a perfectly valid token belongs to a non-admin user hitting an
 * admin-only endpoint. Logging that user out would be wrong, so the two cases
 * are told apart by looking at the token we hold locally.
 */

const SESSIONS = {
  manager: {
    clear: logout,
    loginPath: "/app/access/marketers",
    // getSessionToken() is null when the JWT is missing or its exp has passed.
    // A token that still looks valid means the server refused it for some
    // other reason - most often the user simply lacks the right - so we err
    // towards keeping the user logged in.
    isDead: () => !getSessionToken(),
  },
};

export const SESSION_NOTICE_EVENT = "opasti:session-notice";

export const EXPIRED = "expired";
export const DENIED = "denied";

// One notice at a time. A page that fires five requests in parallel gets five
// 401s, and the user should still see a single message.
let pending = null;

const dispatch = (notice) => {
  pending = notice;
  window.dispatchEvent(
    new window.CustomEvent(SESSION_NOTICE_EVENT, { detail: notice }),
  );
};

/**
 * Call on any 401 from the API. Clears the session and asks the user to log in
 * again when the token is dead; otherwise reports it as a permission problem
 * and leaves the session alone.
 *
 * @param {"manager"} kind which token the failed request used
 * @returns {boolean} true when the session was ended
 */
export const handleUnauthorized = (kind = "manager") => {
  if (!isBrowser()) return false;

  const session = SESSIONS[kind] ?? SESSIONS.manager;

  if (!session.isDead()) {
    // A locally-valid token can still be refused server-side, e.g. right
    // after a JWT secret rotation, so the notice carries a loginPath in case
    // the user wants to log out and back in rather than just dismiss.
    if (!pending) dispatch({ type: DENIED, loginPath: session.loginPath });
    return false;
  }

  session.clear();
  // An ended session outranks a permission notice that is already showing:
  // that one has no way back, this one does.
  if (!pending || pending.type === DENIED) {
    dispatch({ type: EXPIRED, loginPath: session.loginPath });
  }
  return true;
};

/**
 * Proactive check for a tab left open until the token ran out, so the user is
 * told before their next click fails.
 */
export const checkSessionExpiry = () => {
  if (!isBrowser()) return;
  // The manager JWT is only used under /app, so a stale token left in storage
  // must not interrupt someone reading the public pages.
  if (!window.location.pathname.startsWith("/app")) return;
  if (isSessionExpired()) handleUnauthorized("manager");
};

/** Subscribe to session notices. Returns an unsubscribe function. */
export const onSessionNotice = (listener) => {
  if (!isBrowser()) return () => {};
  const handler = (event) => listener(event.detail);
  window.addEventListener(SESSION_NOTICE_EVENT, handler);
  return () => window.removeEventListener(SESSION_NOTICE_EVENT, handler);
};

/** The user acknowledged the notice. An ended session sends them to log in. */
export const dismissSessionNotice = () => {
  const notice = pending;
  pending = null;
  if (notice && notice.type === EXPIRED) navigate(notice.loginPath);
};

/**
 * The user chose to log out and back in from a permission notice, rather than
 * just dismissing it - most often because a JWT secret rotation left them
 * holding a locally-valid token the server no longer accepts.
 */
export const reauthenticate = () => {
  const notice = pending;
  pending = null;
  logout(() => navigate(notice?.loginPath ?? SESSIONS.manager.loginPath));
};
